"use client";

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Mail,
  Download,
} from "lucide-react";
import type { Product } from "../../../lib/types";
import { JUHUU } from "@juhuu/sdk-ts";
import { juhuu } from "../../../juhuuClass";
import CTASection from "../../../components/cta-section";

// Define category colors
const categoryColors: Record<string, { bg: string; text: string }> = {
  "Mobility Solutions": { bg: "bg-blue-100", text: "text-blue-800" },
  "Smart Infrastructure": { bg: "bg-green-100", text: "text-green-800" },
  "Digital Twins": { bg: "bg-purple-100", text: "text-purple-800" },
  "Data Analytics": { bg: "bg-amber-100", text: "text-amber-800" },
  "User Access": { bg: "bg-red-100", text: "text-red-800" },
  "Booking Systems": { bg: "bg-cyan-100", text: "text-cyan-800" },
  "Payment Solutions": { bg: "bg-emerald-100", text: "text-emerald-800" },
  "Integration Tools": { bg: "bg-indigo-100", text: "text-indigo-800" },
};

// Sample images
const productImages: Record<string, string[]> = {
  "bikebox-smart-parking": [
    "/smart-bicycle-parking.png",
    "/smart-bicycle-parking.png",
    "/bicycle-parking-technology.png",
  ],
  "urbanmobility-fleet-manager": [
    "/bike-fleet-management.png",
    "/bike-fleet-dashboard.png",
    "/fleet-management-system.png",
  ],
};

// Reference documents
const referenceDocuments = [
  {
    title: "Integration Guide",
    description:
      "Step-by-step guide for integrating products with your existing systems",
    image: "/integration-documentation.png",
    url: "https://docs.juhuu.app/integration-guide",
  },
  {
    title: "API Documentation",
    description: "Complete API reference for developers",
    image: "/api-documentation.png",
    url: "https://docs.juhuu.app/api-reference",
  },
  {
    title: "Best Practices",
    description: "Recommended approaches for optimal implementation",
    image: "/best-practices-guide.png",
    url: "https://docs.juhuu.app/best-practices",
  },
];

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  //console.log(id);
  const [product, setProduct] = useState<JUHUU.Product.Object | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [property, setProperty] = useState<JUHUU.Property.Object | null>(null);

  // fetch property once product is fetched
  useEffect(() => {
    const fetchProperty = async () => {
      if (product && product.propertyId) {
        const propQuery = await juhuu.properties.retrieve({
          propertyId: product.propertyId,
        });
        if (propQuery.ok) {
          setProperty(propQuery.data.property);
        }
      }
    };
    fetchProperty();
  }, [product]);

  const handleRefresh = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    const query = await juhuu.products.retrieve({ productId: id });

    if (query.ok === false) {
      setProduct(null);
      setLoading(false);
      return;
    }

    setProduct(query.data.product);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-4xl md:text-6xl font-bold mb-4">
          Product not found
        </h1>
        <a href="/marketplace" className="text-purple-600 hover:underline">
          Return to marketplace
        </a>
      </div>
    );
  }
  // Get product images or use default
  const images = productImages[product.id] || [product.bannerImageLight];

  // Navigate to next image
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  // Navigate to previous image
  const prevImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  // Construct the docs URL based on product slug
  const docsUrl = `https://docs.juhuu.app/articles/67039dcc35ed1ec9444a7159`;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb - Updated style */}
        <div className="mb-8">
          <a
            href="/marketplace"
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-500 via-purple-600 to-purple-500 rounded-md shadow-sm text-sm font-medium text-white hover:from-red-600 hover:via-purple-700 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to marketplace
          </a>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Product Header */}
          <div className="p-6 sm:p-8 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center">
                <div className="mr-4"></div>
                <div>
                  <h1 className="text-4xl md:text-6xl font-bold text-gray-900">
                    {product.name}
                  </h1>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-gray-500 mr-2">
                      Controlled by
                    </span>
                    <span className="font-medium text-gray-700">JUHUU</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Content */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-6 sm:p-8">
            {/* Left Column - Image and Details */}
            <div className="md:col-span-8">
              <div className="mb-8">
                {/* Image Gallery */}
                <div className="relative aspect-w-16 aspect-h-9 bg-gray-200 rounded-xl overflow-hidden">
                  <img
                    src={images[currentImageIndex] || "/placeholder.svg"}
                    alt={`${product.name} - Image ${currentImageIndex + 1}`}
                    width={800}
                    height={600}
                    className="w-full h-full object-cover"
                  />

                  {/* Navigation arrows - only show if there are multiple images */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all"
                        aria-label="Next image"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>

                      {/* Image counter */}
                      <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Thumbnail navigation */}
                {images.length > 1 && (
                  <div className="flex mt-3 space-x-2 overflow-x-auto pb-2">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                          currentImageIndex === index
                            ? "border-purple-500 opacity-100"
                            : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={image || "/placeholder.svg"}
                          alt={`Thumbnail ${index + 1}`}
                          width={64}
                          height={64}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Description
                </h2>
                <p className="text-gray-700 whitespace-pre-line">
                  {product.description.en}
                </p>
              </div>

              {/* Key Features - Moved and redesigned */}
              <div className="mb-8 bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Key Features
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.technologyArray.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                        <svg
                          className="h-4 w-4 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download Area - New section */}
              {/* <div className="mb-8 bg-purple-50 rounded-xl p-6 border border-purple-100">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Download Area
                </h2>
                <p className="text-gray-700 mb-4">
                  Access product resources, documentation, and support materials
                  from our secure download area.
                </p>
                <div className="space-y-3">
                  <a
                    href={`https://juhuu.sharepoint.com/sites/marketplace/${product.id}/resources`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-purple-600 hover:text-purple-800"
                  >
                    <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <Download className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-medium">Product Documentation</span>
                      <p className="text-sm text-gray-600">PDF, 2.4 MB</p>
                    </div>
                  </a>

                  <a
                    href={`https://juhuu.sharepoint.com/sites/marketplace/${product.id}/technical-specs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-purple-600 hover:text-purple-800"
                  >
                    <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <Download className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-medium">
                        Technical Specifications
                      </span>
                      <p className="text-sm text-gray-600">PDF, 1.8 MB</p>
                    </div>
                  </a>

                  <a
                    href={`https://juhuu.sharepoint.com/sites/marketplace/${product.id}/integration-guide`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-purple-600 hover:text-purple-800"
                  >
                    <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <Download className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-medium">Integration Guide</span>
                      <p className="text-sm text-gray-600">PDF, 3.2 MB</p>
                    </div>
                  </a>
                </div>
              </div> */}

              {/* Reference Documents Section */}
              {/* <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Reference Documents
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {referenceDocuments.map((doc, index) => (
                    <a
                      key={index}
                      href={doc.url}
                      target="_blank"
                      className="block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="aspect-w-16 aspect-h-9 bg-gray-100">
                        <img
                          src={doc.image || "/placeholder.svg"}
                          alt={doc.title}
                          width={200}
                          height={120}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-1">
                          {doc.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {doc.description}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div> */}

              {/* Technical Information Section with JUHUU Docs Icon - Full Width */}
              {/* <div className="mb-8 -mx-8 sm:-mx-12 md:-mx-16 lg:-mx-24 px-8 col-span-full w-full">
                <div className="flex items-center mb-4">
                  <img
                    src="/docs-icon.png"
                    alt="JUHUU Docs"
                    width={28}
                    height={28}
                    className="mr-2"
                  />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Technical Information
                  </h2>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 -mx-8 sm:-mx-12 md:-mx-16 lg:-mx-24 px-8 sm:px-12 md:px-16 lg:px-24">
                  <div className="mb-6">
                    <h3 className="text-base font-medium text-gray-900 mb-3">
                      Documentation
                    </h3>
                    <div className="w-full h-[650px] border border-gray-200 rounded-lg overflow-hidden">
                      <iframe
                        src={docsUrl}
                        title="JUHUU Documentation"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Right Column - CTA and Info */}
            <div className="md:col-span-4">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 sticky top-6">
                <div className="mb-6">
                  {/* <img
                    src={product.manufacturer.logo || "/placeholder.svg"}
                    alt={`${product.manufacturer.name} logo`}
                    width={180}
                    height={60}
                    className="h-12 w-auto object-contain"
                  /> */}
                </div>

                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Get Started
                </h3>

                <div className="space-y-3">
                  {/* Contact Partner button styled to match the rest of the site */}
                  <a
                    href={`mailto:${property?.email}?subject=Inquiry about ${product.name}`}
                    className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-red-500 via-purple-600 to-purple-500 rounded-md shadow-sm text-sm font-medium text-white hover:from-red-600 hover:via-purple-700 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Partner
                  </a>

                  {/* Keep the Read more button */}
                  <a
                    href={`https://docs.juhuu.app/articles/${product.articleId}`}
                    className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read more <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Categories
                  </h4>

                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {product.purposeArray.map((tag, index) => (
                      <>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                          {tag.sector.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                          {tag.category.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                          {tag.modality.toUpperCase()}
                        </span>
                      </>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="contact">
        <CTASection />
      </div>
    </div>
  );
}
